/**
 * @copyright Copyright (c) Open-Xchange GmbH, Germany <info@open-xchange.com>
 * @license AGPL-3.0
 *
 * This code is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with OX App Suite. If not, see <https://www.gnu.org/licenses/agpl-3.0.txt>.
 *
 * Any use of the work other than as authorized under this license or copyright law is prohibited.
 */

import http from '@/io.ox/core/http'
import ox from '@/ox.js'
import ky from 'ky'

let inflight = null

/**
 * Returns an existing - and not yet expired - or fresh JWT
 * @param {boolean} [renew]  Enforce fetching a fresh JWT
 * @returns {string|null}  JWT or null if fetching failed despite retries
 */
export async function getJWT ({ renew = false } = {}) {
  if (!renew) {
    const jwt = getJWTFromSessionStorage()
    if (jwt && jwt.expires > (new Date().valueOf() + 10_000)) return jwt.token
  }
  if (inflight) return inflight
  return (inflight = fetchJWT().finally(() => { inflight = null }))
}

function getJWTFromSessionStorage () {
  try {
    const item = sessionStorage.getItem('coreJWT')
    if (!item) return null
    return JSON.parse(item)
  } catch (err) {
    return null
  }
}

async function fetchJWT () {
  try {
    // get token from App Suite (Java) Middleware
    const { token } = await http.GET({
      module: 'token',
      params: { action: 'acquireToken' }
    })
    // handover to UI core service
    const json = { token }
    const client = ky.create({
      backoffLimit: 1000,
      // 7 attempts with: 50ms, 100ms, 200ms, 400ms, 800ms, 1000ms, 1000ms. total: 3.550ms
      delay (attempt) { return 0.05 * (2 ** (attempt - 1)) * 1000 },
      prefixUrl: ox.root,
      limit: 7
    })
    const response = await client.post('ui/v1/token', { json }).json()
    sessionStorage.setItem('coreJWT', JSON.stringify(response))
    return response.token
  } catch (err) {
    console.error('Failed to fetch JWT', err)
    return null
  }
}

if (ox.debug) {
  window.getJWT = getJWT
  window.fetchJWT = fetchJWT
}
